import React, { useCallback, useState } from 'react'
import { Box, Button, Collapse, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { useNamesTranslation } from '../hooks/useNamesTranslation'
import { DATE_RANGES, dayOf, HISTORY_TEXT_MAX, KIND_FILTERS, localMidnightOf, STATUS_FILTERS } from '../model/historyQuery'
import CorrectionDetail from './CorrectionDetail'
import HistoryList from './HistoryList'

// A day `yyyy-MM-dd` of the filters as the local date the picker shows, null when empty.
const dateOfDay = (day) => (day ? new Date(localMidnightOf(day)) : null)

/**
 * The second row of the filters (RN1, RN3): Type and the two date ranges, folded under «More filters». No filter by order or
 * job (RN2). A crossed range shows its error under the «to» field and nothing is searched (the hook).
 */
const MoreFilters = ({ history }) => {
  const { t_ } = useNamesTranslation()
  const { filters, locked } = history
  return (
    <Box data-testid='more-filters' sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px 14px', alignItems: 'flex-start', pt: 1.25, mt: 1.25, borderTop: 1, borderColor: 'divider' }}>
      <TextField
        select size='small' label={t_('Type')} value={filters.kind} disabled={locked} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
        onChange={(event) => history.handleFilter('kind', event.target.value)}
      >
        {KIND_FILTERS.map((option) => <option key={option.value} value={option.value}>{t_(option.label)}</option>)}
      </TextField>
      {DATE_RANGES.map((range) => {
        const error = history.dateErrors[range.name]
        return [['fromDay', range.fromLabel], ['toDay', range.toLabel]].map(([end, label]) => (
          <DatePicker
            key={`${range.name}-${end}`}
            label={t_(label)}
            value={dateOfDay(filters[range.name][end])}
            disabled={locked}
            onChange={(date) => {
              // Typing: an incomplete date changes nothing until it is a day; emptying the field empties the limit.
              if (date === null) history.handleDay(range.name, end, '')
              else if (dayOf(date) !== '') history.handleDay(range.name, end, dayOf(date))
            }}
            slotProps={{
              textField: {
                size: 'small',
                error: Boolean(error),
                helperText: error && end === 'toDay' ? t_(error.key, error.params) : undefined
              }
            }}
          />
        ))
      })}
    </Box>
  )
}

/**
 * The «Correction history» tab (maqueta `#view-h`): Search, Machine, Worker and Status over the list, a folded «More filters»
 * row (Type and dates), and the detail of the chosen correction beside it (master-detail). `history` is what `useHistory`
 * returns; the machine and worker options are the catalogs the timelines already use. `canRequest` offers reverting;
 * `onShowInTimeline(target)` takes the correction to the timelines tab (RN16).
 */
const CorrectionHistory = ({ history, deviceOptions, workerOptions, canRequest = false, onShowInTimeline }) => {
  const { t_ } = useNamesTranslation()
  const [moreOpen, setMoreOpen] = useState(false)
  const { filters, list, selected, reasons, locked } = history
  const reasonLabel = useCallback((code) => {
    const reason = reasons.find((candidate) => candidate.code === code)
    return reason ? t_(reason.label) : code
  }, [reasons, t_])

  return (
    <Box>
      <Box sx={{ p: 1.25, mb: 1.25, border: 1, borderColor: 'divider', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px 14px', alignItems: 'flex-end' }}>
          <TextField
            size='small' type='search' disabled={locked} label={t_('Search')} value={history.textInput} sx={{ flex: 1, minWidth: 220 }}
            placeholder={t_('Worker, machine, job or order')}
            inputProps={{ maxLength: HISTORY_TEXT_MAX }} InputLabelProps={{ shrink: true }}
            onChange={(event) => history.handleTextInput(event.target.value)}
          />
          <TextField
            select size='small' label={t_('Machine')} disabled={locked} value={String(filters.deviceId)} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
            onChange={(event) => history.handleFilter('deviceId', event.target.value)}
          >
            <option value=''>{t_('All machines')}</option>
            {deviceOptions.map((device) => <option key={device.id} value={String(device.id)}>{device.label}</option>)}
          </TextField>
          <TextField
            select size='small' label={t_('Worker')} disabled={locked} value={String(filters.workerId)} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
            onChange={(event) => history.handleFilter('workerId', event.target.value)}
          >
            <option value=''>{t_('All')}</option>
            {workerOptions.map((worker) => <option key={worker.id} value={String(worker.id)}>{worker.label}</option>)}
          </TextField>
          <Box>
            <Typography variant='caption' component='div' color='text.secondary'>{t_('Status')}</Typography>
            <ToggleButtonGroup
              size='small' exclusive disabled={locked} value={filters.status} aria-label={t_('Status')}
              onChange={(_event, status) => { if (status) history.handleFilter('status', status) }}
            >
              {STATUS_FILTERS.map((option) => <ToggleButton key={option.value} value={option.value}>{t_(option.label)}</ToggleButton>)}
            </ToggleButtonGroup>
          </Box>
          <Button size='small' disabled={locked} aria-expanded={moreOpen} onClick={() => setMoreOpen((open) => !open)}>{t_('More filters')}</Button>
        </Box>
        <Collapse in={moreOpen} unmountOnExit><MoreFilters history={history} /></Collapse>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(280px, 380px) minmax(0, 1fr)' }, gap: 1.5, alignItems: 'start' }}>
        <HistoryList
          list={list} page={history.page} selectedId={selected.id} disabled={locked}
          onSelect={history.handleSelect} onPage={history.handlePage} onRetry={history.handleRetryList}
        />
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, minWidth: 0 }}>
          <CorrectionDetail history={history} canRequest={canRequest} reasonLabel={reasonLabel} onShowInTimeline={onShowInTimeline} />
        </Box>
      </Box>
    </Box>
  )
}

export default CorrectionHistory
